import { jsPDF } from 'jspdf';

export interface PdfExercise {
  name:        string;
  series:      string;
  volumeValue: string;
  volumeUnit:  'reps' | 'seconds';
  load:        string;
  notes:       string;
}

export interface PlanPdfOptions {
  patientName:  string;
  planNumber:   number;
  objective:    string;
  fechaInicio?: string | null;
  exercises:    PdfExercise[];
  generatedBy?: string;
}

// ─── Color helpers ────────────────────────────────────────────────────────────

type RGB = [number, number, number];

const BRAND:  RGB = [99,  102, 241];
const DARK:   RGB = [30,  30,  46 ];
const MUTED:  RGB = [120, 120, 140];
const WHITE:  RGB = [255, 255, 255];
const LIGHT:  RGB = [245, 245, 250];
const BORDER: RGB = [220, 220, 230];

function fill(doc: jsPDF, c: RGB) { doc.setFillColor(c[0], c[1], c[2]); }
function stroke(doc: jsPDF, c: RGB) { doc.setDrawColor(c[0], c[1], c[2]); }
function color(doc: jsPDF, c: RGB) { doc.setTextColor(c[0], c[1], c[2]); }

function formatFecha(d: string) {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

// ─── Main generator ───────────────────────────────────────────────────────────

export function generatePlanPdf(opts: PlanPdfOptions): Blob {
  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W      = 210;
  const M      = 16;   // margin
  const INNER  = W - M * 2;
  let   y      = 0;

  // ── Header bar ─────────────────────────────────────────────────────────────
  fill(doc, BRAND);
  doc.rect(0, 0, W, 22, 'F');

  color(doc, WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('FisioLab', M, 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Rutina de Ejercicios — Plan de Tratamiento', M + 38, 14);

  const today = new Date().toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
  doc.text(today, W - M, 14, { align: 'right' });

  y = 30;

  // ── Patient + Plan info box ─────────────────────────────────────────────────
  fill(doc, LIGHT);
  stroke(doc, BORDER);
  doc.roundedRect(M, y, INNER, 28, 3, 3, 'FD');

  color(doc, MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('PACIENTE', M + 6, y + 7);
  doc.text(`PLAN #${opts.planNumber}`, M + INNER / 2 + 4, y + 7);

  color(doc, DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(opts.patientName, M + 6, y + 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Plan #${opts.planNumber}`, M + INNER / 2 + 4, y + 14);

  if (opts.fechaInicio) {
    color(doc, MUTED);
    doc.setFontSize(8);
    doc.text(`Inicio: ${formatFecha(opts.fechaInicio)}`, M + INNER / 2 + 4, y + 21);
  }

  y += 36;

  // ── Objective ───────────────────────────────────────────────────────────────
  fill(doc, BRAND);
  doc.rect(M, y, 3, 12, 'F');

  color(doc, MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('OBJETIVO TERAPÉUTICO', M + 7, y + 5);

  color(doc, DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const objLines = doc.splitTextToSize(opts.objective, INNER - 12);
  doc.text(objLines, M + 7, y + 13);
  y += 13 + (objLines as string[]).length * 5 + 6;

  // ── Section title bar ───────────────────────────────────────────────────────
  fill(doc, BRAND);
  doc.rect(M, y, INNER, 9, 'F');
  color(doc, WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('EJERCICIOS PRESCRITOS', M + 4, y + 6);
  doc.text(`${opts.exercises.length} ejercicio${opts.exercises.length !== 1 ? 's' : ''}`, W - M - 4, y + 6, { align: 'right' });
  y += 13;

  // ── Exercise rows ───────────────────────────────────────────────────────────
  opts.exercises.forEach((ex, idx) => {
    const noteLines  = ex.notes ? (doc.splitTextToSize(`  ${ex.notes}`, INNER - 22) as string[]) : [];
    const rowH       = 22 + (noteLines.length > 0 ? noteLines.length * 4 + 4 : 0);

    if (y + rowH > 282) {
      doc.addPage();
      y = 16;
    }

    const rowBg: RGB = idx % 2 === 0 ? WHITE : LIGHT;
    fill(doc, rowBg);
    stroke(doc, BORDER);
    doc.rect(M, y, INNER, rowH, 'FD');

    // Number badge
    fill(doc, BRAND);
    doc.circle(M + 7, y + 8, 4.5, 'F');
    color(doc, WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(String(idx + 1), M + 7, y + 10.5, { align: 'center' });

    // Name
    color(doc, DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(ex.name, M + 15, y + 9);

    // Dosage line
    const volumeLabel = ex.volumeUnit === 'reps' ? `${ex.volumeValue} reps` : `${ex.volumeValue} seg`;
    const dosage      = `${ex.series} series × ${volumeLabel}`;
    const load        = ex.load || 'Sin carga';

    color(doc, MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(dosage, M + 15, y + 16);

    const dW = doc.getTextWidth(dosage);
    doc.text('·', M + 15 + dW + 2, y + 16);

    color(doc, DARK);
    doc.text(load, M + 15 + dW + 6, y + 16);

    // Notes
    if (noteLines.length > 0) {
      color(doc, MUTED);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.text(noteLines, M + 15, y + 22);
    }

    y += rowH + 2;
  });

  // ── Footer on every page ────────────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    stroke(doc, BORDER);
    doc.line(M, 287, W - M, 287);
    color(doc, MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('FisioLab — Sistema de Gestión Clínica', M, 292);
    if (opts.generatedBy) {
      doc.text(`Generado por: ${opts.generatedBy}`, W / 2, 292, { align: 'center' });
    }
    doc.text(`Página ${i} de ${pages}`, W - M, 292, { align: 'right' });
  }

  return doc.output('blob');
}

// ─── Phone formatting ─────────────────────────────────────────────────────────

export function formatPhoneForWhatsApp(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('09') && digits.length === 10) return `593${digits.slice(1)}`;
  if (digits.startsWith('593')) return digits;
  return digits || null;
}
